import { DS_TOOLTIP_ANCHOR_NAME } from "../constants/attributes";
import { EXIT_TRANSITION_MS, HIDE_DELAY_MS, SHOW_DELAY_MS, WARM_MS } from "../constants/timing";
import type { ActiveTooltip } from "../types/controller";
import type { DSTooltipOptions } from "../types/options";
import {
  addTooltipDescription,
  clearTooltipAnchorName,
  eventAnchor,
  getOrCreateTooltip,
  measureResolvedTooltipPlacement,
  parseTooltipOptions,
  removeTooltipDescription,
  renderTooltipContent,
  setRequestedTooltipPlacement,
  setResolvedTooltipPlacement,
  setTooltipAnchorName,
} from "./tooltipDom";
import { callPopoverSafely, toggleState } from "./tooltipPopover";

export class TooltipController {
  private readonly ownerDocument: Document;
  private readonly ownerWindow: Window;
  private listenerSignal?: AbortSignal;
  private hoverAnchor: HTMLElement | null = null;
  private focusAnchor: HTMLElement | null = null;
  private readonly dismissedAnchors = new WeakSet<HTMLElement>();
  private active?: ActiveTooltip;
  private tooltip?: HTMLElement;
  private isOpen = false;
  // A close we initiate still dispatches a toggle event; it must not count as a native light dismiss.
  private suppressNextClose = false;
  private pendingShowAnchor: HTMLElement | null = null;
  private showTimer?: number;
  private hideTimer?: number;
  private warmUntil = 0;
  private exitingAnchor?: HTMLElement;
  private exitTimer?: number;
  private trackingAbortController?: AbortController;
  private resizeObserver?: ResizeObserver;
  private measureFrame?: number;

  constructor(ownerDocument: Document = document) {
    this.ownerDocument = ownerDocument;
    this.ownerWindow = ownerDocument.defaultView ?? window;
  }

  start(signal: AbortSignal): void {
    if (this.listenerSignal) return;
    this.listenerSignal = signal;
    const options = { signal };
    this.ownerDocument.addEventListener("mouseover", this.handleMouseOver, options);
    this.ownerDocument.addEventListener("mouseout", this.handleMouseOut, options);
    this.ownerDocument.addEventListener("focusin", this.handleFocusIn, options);
    this.ownerDocument.addEventListener("focusout", this.handleFocusOut, options);
    this.ownerDocument.addEventListener("keydown", this.handleKeyDown, options);
    this.ownerDocument.addEventListener("pointerdown", this.handlePointerDown, options);
  }

  hideForAnchor(anchor: HTMLElement): void {
    this.releaseOwnership(anchor);
    if (this.pendingShowAnchor === anchor) this.cancelShow();
    if (this.active?.anchor === anchor) this.hideImmediately();
    if (this.exitingAnchor === anchor) this.finishExit();
    this.reconcileOwnership();
  }

  refreshForAnchor(anchor: HTMLElement): void {
    const options = parseTooltipOptions(anchor);
    if (this.active?.anchor === anchor) {
      if (!options) this.hideForAnchor(anchor);
      else {
        this.active.options = options;
        addTooltipDescription(anchor);
        this.renderTooltip(options);
        this.scheduleMeasurement();
      }
      return;
    }

    if (!this.canClaimCurrentOwnership(anchor, options)) return;
    this.claimCurrentOwnership(anchor);
  }

  reopenForAnchor(anchor: HTMLElement): void {
    this.dismissedAnchors.delete(anchor);
    this.refreshForAnchor(anchor);
  }

  private canClaimCurrentOwnership(anchor: HTMLElement, options: DSTooltipOptions | null): options is DSTooltipOptions {
    return Boolean(options) && !this.dismissedAnchors.has(anchor);
  }

  private claimCurrentOwnership(anchor: HTMLElement): void {
    if (anchor.contains(this.ownerDocument.activeElement)) this.focusAnchor = anchor;
    if (anchor.matches(":hover")) this.hoverAnchor = anchor;
    this.reconcileOwnership();
  }

  destroy(): void {
    this.cancelShow();
    this.cancelHide();
    this.stopPositionTracking();
    this.unanchorActive();
    this.finishExit();
    this.closePopover();
    this.removeTooltip();
  }

  private readonly handleMouseOver = (event: MouseEvent): void => {
    const anchor = eventAnchor(event.target);
    if (anchor === eventAnchor(event.relatedTarget)) return;
    if (anchor) this.dismissedAnchors.delete(anchor);
    this.hoverAnchor = anchor;
    this.reconcileOwnership();
  };

  private readonly handleMouseOut = (event: MouseEvent): void => {
    const anchor = eventAnchor(event.target);
    const relatedAnchor = eventAnchor(event.relatedTarget);
    if (anchor === relatedAnchor || this.hoverAnchor !== anchor) return;
    this.hoverAnchor = relatedAnchor;
    this.reconcileOwnership();
  };

  private readonly handleFocusIn = (event: FocusEvent): void => {
    const anchor = eventAnchor(event.target);
    if (anchor === eventAnchor(event.relatedTarget)) return;
    if (anchor) this.dismissedAnchors.delete(anchor);
    this.focusAnchor = anchor;
    this.reconcileOwnership();
  };

  private readonly handleFocusOut = (event: FocusEvent): void => {
    const anchor = eventAnchor(event.target);
    const relatedAnchor = eventAnchor(event.relatedTarget);
    if (anchor === relatedAnchor || this.focusAnchor !== anchor) return;
    this.focusAnchor = relatedAnchor;
    this.reconcileOwnership();
  };

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.dismiss();
  };

  private readonly handlePointerDown = (): void => {
    this.dismiss();
  };

  private readonly handleToggle = (event: Event): void => {
    const newState = toggleState(event);
    if (newState === "open") {
      this.isOpen = true;
      this.suppressNextClose = false;
      this.scheduleMeasurement();
      return;
    }
    if (newState !== "closed") return;
    if (this.suppressNextClose) {
      this.suppressNextClose = false;
      return;
    }

    this.isOpen = false;
    this.retireActive();
  };

  private get desiredAnchor(): HTMLElement | null {
    return this.focusAnchor ?? this.hoverAnchor;
  }

  private releaseOwnership(anchor: HTMLElement): void {
    if (this.hoverAnchor === anchor) this.hoverAnchor = null;
    if (this.focusAnchor === anchor) this.focusAnchor = null;
  }

  private reconcileOwnership(): void {
    const desiredAnchor = this.desiredAnchor;
    if (desiredAnchor) this.reconcileWithOwner(desiredAnchor);
    else this.reconcileWithoutOwner();
  }

  private reconcileWithoutOwner(): void {
    this.cancelShow();
    if (this.active) this.scheduleHide();
  }

  private reconcileWithOwner(desiredAnchor: HTMLElement): void {
    this.cancelHide();
    if (this.active?.anchor === desiredAnchor) return;
    if (this.shouldShowImmediately(desiredAnchor)) this.show(desiredAnchor);
    else this.scheduleShow(desiredAnchor);
  }

  private shouldShowImmediately(anchor: HTMLElement): boolean {
    return this.focusAnchor === anchor || Boolean(this.active) || Date.now() < this.warmUntil;
  }

  private scheduleShow(anchor: HTMLElement): void {
    if (this.pendingShowAnchor === anchor) return;
    this.cancelShow();
    this.pendingShowAnchor = anchor;
    this.showTimer = this.ownerWindow.setTimeout(() => {
      this.showTimer = undefined;
      this.pendingShowAnchor = null;
      if (this.focusAnchor === anchor || this.hoverAnchor === anchor) this.show(anchor);
    }, SHOW_DELAY_MS);
  }

  private scheduleHide(): void {
    if (this.hideTimer !== undefined) return;
    this.hideTimer = this.ownerWindow.setTimeout(() => {
      this.hideTimer = undefined;
      if (this.desiredAnchor) this.reconcileOwnership();
      else this.hideImmediately();
    }, HIDE_DELAY_MS);
  }

  private dismiss(): void {
    const anchor = this.active?.anchor ?? this.pendingShowAnchor ?? this.desiredAnchor;
    if (anchor) this.dismissedAnchors.add(anchor);
    this.hoverAnchor = null;
    this.focusAnchor = null;
    this.cancelShow();
    this.cancelHide();
    this.hideImmediately();
  }

  private show(anchor: HTMLElement): void {
    const options = parseTooltipOptions(anchor);
    if (!options) return;

    this.cancelShow();
    this.cancelHide();
    this.finishExit();
    this.unanchorActive();

    this.active = { anchor, options };
    addTooltipDescription(anchor);
    setTooltipAnchorName(anchor);
    const tooltip = this.renderTooltip(options);
    if (!this.openPopover(tooltip)) {
      this.unanchorActive();
      return;
    }

    this.startPositionTracking(tooltip, anchor);
    this.scheduleMeasurement();
  }

  private hideImmediately(): void {
    if (!this.retireActive()) return;
    if (!this.closePopover()) this.finishExit();
  }

  private releaseActive(): HTMLElement | undefined {
    if (!this.active) return undefined;
    const { anchor } = this.active;
    this.active = undefined;
    removeTooltipDescription(anchor);
    return anchor;
  }

  private unanchorActive(): void {
    const anchor = this.releaseActive();
    if (anchor) clearTooltipAnchorName(anchor);
  }

  private retireActive(): boolean {
    const anchor = this.releaseActive();
    if (!anchor) return false;
    this.beginExit(anchor);
    this.stopPositionTracking();
    this.warmUntil = Date.now() + WARM_MS;
    return true;
  }

  private renderTooltip(options: DSTooltipOptions): HTMLElement {
    const tooltip = getOrCreateTooltip(this.ownerDocument, this.tooltip);
    if (tooltip !== this.tooltip) {
      tooltip.addEventListener("toggle", this.handleToggle, { signal: this.listenerSignal });
      this.tooltip = tooltip;
    }
    renderTooltipContent(tooltip, options);
    setRequestedTooltipPlacement(tooltip, options.placement);
    setResolvedTooltipPlacement(tooltip, options.placement);
    tooltip.style.setProperty("position-anchor", DS_TOOLTIP_ANCHOR_NAME);
    return tooltip;
  }

  private openPopover(tooltip: HTMLElement): boolean {
    if (this.isOpen) return true;
    if (!callPopoverSafely(() => tooltip.showPopover())) {
      this.removeTooltip();
      return false;
    }
    this.isOpen = true;
    return true;
  }

  private closePopover(): boolean {
    const tooltip = this.tooltip;
    if (!tooltip || !this.isOpen) return false;
    this.isOpen = false;
    if (!callPopoverSafely(() => tooltip.hidePopover())) {
      this.removeTooltip();
      return false;
    }
    this.suppressNextClose = true;
    return true;
  }

  private removeTooltip(): void {
    this.tooltip?.remove();
    this.tooltip = undefined;
    this.isOpen = false;
    this.suppressNextClose = false;
  }

  private beginExit(anchor: HTMLElement): void {
    this.finishExit();
    this.exitingAnchor = anchor;
    this.exitTimer = this.ownerWindow.setTimeout(this.finishExit, EXIT_TRANSITION_MS);
  }

  private readonly finishExit = (): void => {
    if (this.exitTimer !== undefined) this.ownerWindow.clearTimeout(this.exitTimer);
    this.exitTimer = undefined;
    if (this.exitingAnchor) clearTooltipAnchorName(this.exitingAnchor);
    this.exitingAnchor = undefined;
  };

  private cancelShow(): void {
    if (this.showTimer !== undefined) this.ownerWindow.clearTimeout(this.showTimer);
    this.showTimer = undefined;
    this.pendingShowAnchor = null;
  }

  private cancelHide(): void {
    if (this.hideTimer !== undefined) this.ownerWindow.clearTimeout(this.hideTimer);
    this.hideTimer = undefined;
  }

  private startPositionTracking(tooltip: HTMLElement, anchor: HTMLElement): void {
    this.stopPositionTracking();
    const abortController = new AbortController();
    this.trackingAbortController = abortController;
    const options = { capture: true, signal: abortController.signal };
    this.ownerDocument.addEventListener("scroll", this.scheduleMeasurement, options);
    this.ownerWindow.addEventListener("resize", this.scheduleMeasurement, options);

    if (typeof ResizeObserver === "function") {
      this.resizeObserver = new ResizeObserver(this.scheduleMeasurement);
      this.resizeObserver.observe(tooltip);
      this.resizeObserver.observe(anchor);
    }
  }

  private stopPositionTracking(): void {
    this.trackingAbortController?.abort();
    this.trackingAbortController = undefined;
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    if (this.measureFrame !== undefined) this.ownerWindow.cancelAnimationFrame(this.measureFrame);
    this.measureFrame = undefined;
  }

  private readonly scheduleMeasurement = (): void => {
    if (!this.active || !this.tooltip || this.measureFrame !== undefined) return;
    this.measureFrame = this.ownerWindow.requestAnimationFrame(() => {
      this.measureFrame = undefined;
      if (!this.active || !this.tooltip) return;
      if (!this.active.anchor.isConnected) {
        this.hideImmediately();
        return;
      }

      const { anchor, options } = this.active;
      const resolvedPlacement = measureResolvedTooltipPlacement(anchor, this.tooltip, options.placement);
      setResolvedTooltipPlacement(this.tooltip, resolvedPlacement);
    });
  };
}
